import {
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChild,
  TemplateRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * CollapsibleSectionComponent provides a titled, collapsible panel with optional "Done" action and content projection.
 * It manages expanded/collapsed state, emits changes, and can render a section body via a TemplateRef content child.
 * @component
 * @selector app-collapsible-section
 * @example
 *   <app-collapsible-section
 *     [title]="'Section Title'"
 *     [isExpanded]="false"
 *     [showDoneButton]="true"
 *     [doneButtonText]="'Finish'"
 *     [isCompleted]="false"
 *     (toggle)="onSectionToggle($event)"
 *     (done)="onSectionDone()">
 *     <ng-template #sectionContent>
 *       <!-- section body -->
 *     </ng-template>
 *   </app-collapsible-section>
 * @input {string} title - The text title displayed in the section header.
 * @input {boolean} isExpanded - Controls whether the section is initially expanded (true) or collapsed (false).
 * @input {boolean} showDoneButton - When true, renders a "Done" button in the header/footer for completion actions.
 * @input {string} doneButtonText - Text label for the Done button (defaults to 'Done').
 * @input {boolean} isCompleted - Marks the section visually as completed; consumer can use this to change appearance.
 * @output {Event<boolean>} toggle - Emitted when the section is expanded or collapsed; payload is the new expanded state.
 * @output {Event<void>} done - Emitted when the Done button is clicked.
 *
 * Key Methods:
 * - onToggle(): toggles the internal isExpanded state and emits the toggle event with the new state.
 * - onDone(): emits the done event to notify parent components of completion actions.
 *
 * Content:
 * - Accepts a TemplateRef content child with template variable name "sectionContent" to render the collapsible body.
 *
 * Lifecycle hooks:
 * - No explicit Angular lifecycle hooks are implemented on this component. ContentChild is used to capture projected template content.
 */
@Component({
  selector: 'app-collapsible-section',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './collapsible-section.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './collapsible-section.component.scss',
})
export class CollapsibleSectionComponent {
  @Input() title = '';
  @Input() isExpanded = false;
  @Input() showDoneButton = false;
  @Input() doneButtonText = 'Done';
  @Input() isCompleted = false;
  @Input() headerBadge = '';
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();

  @ContentChild('sectionContent') sectionContent!: TemplateRef<any>;

  onToggle(): void {
    this.isExpanded = !this.isExpanded;
    this.toggle.emit(this.isExpanded);
  }

  onDone(): void {
    this.done.emit();
  }
}
